import { Grid, MenuItem, Select, Button, InputLabel, FormControl, Checkbox, Box, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { Draggable } from 'react-beautiful-dnd';
import { MdDragHandle } from 'react-icons/md';
import { useSelector } from 'react-redux';

import { makeFormStyles } from 'forms/styles';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import IncorrectFieldSet from '../../IncorrectFieldSet';
import { FormTextField } from 'components/TextField';

const SelectAnswerField = ({ option, idx, nodes, node, values, setFieldValue, handleRemove, ...otherProps }) => {
    const { errors } = useFormikContext();
    const theme = useSelector(selectDefaultTheme);
    const validationError = errors?.[`select_options[${idx}].option_text`];
    nodes = nodes.filter((nd) => nd.id !== node.id);
    const commonFormStyles = makeFormStyles(theme);

    return (
        <Draggable
            key={'select_opts_draggable_' + idx}
            draggableId={String('select_opts_draggable_' + idx)}
            index={idx}
        >
            {(provided) => (
                <div ref={provided.innerRef} {...provided.draggableProps}>
                    <Grid
                        container
                        key={'select_opts_' + idx}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '6px',
                        }}
                        {...otherProps}
                    >
                        <Grid
                            display={'flex'}
                            alignItems="center"
                            justifyContent={'center'}
                            item
                            xs={12}
                            sm={12}
                            md={1}
                            lg={1}
                            {...provided.dragHandleProps}
                        >
                            <MdDragHandle className="react-icon" />
                        </Grid>
                        <Grid
                            display={'flex'}
                            alignItems="center"
                            item
                            xs={12}
                            sm={12}
                            md={3}
                            lg={3}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box position="relative" display="flex" flexDirection="column">
                                <FormControl fullWidth>
                                    <FormTextField
                                        label="Text"
                                        placeholder="Text"
                                        name={`select_options[${idx}].option_text`}
                                        onChange={(e) => {
                                            let currSelectOptions = [...values.select_options];
                                            currSelectOptions[idx] = {
                                                ...values.select_options[idx],
                                                option_text: e.target.value,
                                            };
                                            setFieldValue(`select_options`, currSelectOptions);
                                        }}
                                        {...otherProps}
                                        error={!!errors?.[`select_options[${idx}].option_text`]}
                                    />
                                </FormControl>
                                {validationError && (
                                    <Typography
                                        position="absolute"
                                        top="100%"
                                        sx={{
                                            color: '#d32f2f',
                                            fontSize: '0.75rem',
                                            lineHeight: '0.75rem',
                                        }}
                                    >
                                        {validationError}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                        <Grid display={'flex'} alignItems="center" item xs={12} sm={12} md={3} lg={3}>
                            <FormControl fullWidth>
                                <InputLabel id="target-node-id">Target Node</InputLabel>
                                <Select
                                    className={commonFormStyles.materialSelect}
                                    style={{
                                        margin: '0',
                                    }}
                                    labelId="target-node-id"
                                    margin="dense"
                                    id="target_node_id"
                                    label="Target Node"
                                    placeholder="Target Node"
                                    name={`select_options[${idx}].target_node_id`}
                                    value={option?.action?.id}
                                    onChange={(e) => {
                                        let currSelectOptions = [...values.select_options];
                                        currSelectOptions[idx] = {
                                            ...values.select_options[idx],
                                            action: {
                                                ...values.select_options[idx].action,
                                                id: e.target.value,
                                            },
                                        };
                                        setFieldValue('select_options', currSelectOptions);
                                    }}
                                >
                                    <MenuItem key={'next'} value={'next'}>
                                        Next
                                    </MenuItem>
                                    <MenuItem key={'back'} value={'back'}>
                                        Back
                                    </MenuItem>
                                    {nodes?.length > 0 &&
                                        nodes.map((nd) => (
                                            <MenuItem key={nd?.id} value={nd?.id}>
                                                {nd?.title} - #{nd?.id}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid display={'flex'} alignItems="center" item xs={12} sm={12} md={2} lg={2}>
                            <FormControl fullWidth>
                                <FormTextField
                                    label="Answer Value"
                                    placeholder="Answer Value"
                                    name={`select_options[${idx}].answer_value`}
                                    onChange={(e) => {
                                        let currOptions = [...values.select_options];
                                        currOptions[idx] = {
                                            ...values.select_options[idx],
                                            answer_value: e.target.value,
                                        };
                                        setFieldValue(`select_options`, currOptions);
                                    }}
                                    {...otherProps}
                                />
                            </FormControl>
                        </Grid>
                        <Grid display={'flex'} alignItems="center" item xs={12} sm={12} md={1} lg={1}>
                            <FormControl fullWidth>
                                <IncorrectFieldSet>
                                    <Checkbox
                                        color="primary"
                                        checked={Boolean(option?.incorrect)}
                                        onChange={(e) => {
                                            let currSelectOptions = [...values.select_options];
                                            currSelectOptions[idx] = {
                                                ...values.select_options[idx],
                                                incorrect: e.target.checked,
                                            };
                                            setFieldValue('select_options', currSelectOptions);
                                        }}
                                    />
                                </IncorrectFieldSet>
                            </FormControl>
                        </Grid>
                        <Grid display={'flex'} alignItems="center" item xs={12} sm={12} md={1} lg={1}>
                            <FormControl fullWidth>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        handleRemove(idx);
                                    }}
                                >
                                    Remove
                                </Button>{' '}
                            </FormControl>
                        </Grid>
                    </Grid>
                </div>
            )}
        </Draggable>
    );
};

export default SelectAnswerField;
